import { InputWithLabel } from '@adotel/components'
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { Rating } from '@material-ui/lab'
import { useEffect, useState } from 'react'
import React from 'react'
import { useParams } from 'react-router-dom'

import CustomCheckBox from '../../../../components/CustomCheckBox'
import { convertPersianNumbersToEnglishNumbers } from '../../../../helpers/numbers'
import { getTranslate } from '../../../../localization'
import { ResourceType } from '../../../../types/accommodation'
import { CheckBoxDataType } from '../../../../types/customCheckBox'
import '../../../../types/searchAvailability'
import {
  Entities,
  SearchAvailabilityInput
} from '../../../../types/searchAvailability'
import ChannelBalance from '../../../BookingHotelSearchResult/components/ChannelBalance'
import ContentFitList from '../../../BookingHotelSearchResult/components/ContentFitList'
import PriceSlider from '../../../BookingHotelSearchResult/components/PriceSlider'
import useStyles from './styles/index.styles'

type Props = {
  data: Entities[]
  selectedHotelStar: number[]
  setselectedHotelStar: React.Dispatch<React.SetStateAction<number[]>>
  setsearchInputVal: React.Dispatch<React.SetStateAction<string>>
  searchAvailabilityInput: SearchAvailabilityInput
  priceRange: number[]
  maxPrice: number
  setPriceRange: React.Dispatch<React.SetStateAction<number[]>>
  isUserPriceChange: React.MutableRefObject<boolean>
  setSelectedFacilities: React.Dispatch<React.SetStateAction<string[]>>
  setSelectedPropertyType: React.Dispatch<React.SetStateAction<string[]>>
  selectedPropertyType: string[]
  selectedFacilities: string[]
}

const HotelFilters = ({
  data,
  selectedHotelStar,
  setselectedHotelStar,
  setsearchInputVal,
  searchAvailabilityInput,
  priceRange,
  maxPrice,
  setPriceRange,
  isUserPriceChange,
  setSelectedFacilities,
  setSelectedPropertyType,
  selectedPropertyType,
  selectedFacilities
}: Props) => {
  const classes = useStyles()
  const params = useParams<{ channelId: string; code: string; name: string }>()
  const [initialFacilities, setInitialFacilities] = useState<
    CheckBoxDataType[] | null
  >(null)
  const propertyTypeOptions: ResourceType[] = ['Hotel', 'Motel', 'BNB']
  const min = 0
  const handleHotelStarchecked = (value: number) => {
    setselectedHotelStar((prev) => {
      const updated = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]

      return updated
    })
  }
  const handlePriceChange = (newRange: number[]) => {
    isUserPriceChange.current = true
    setPriceRange(newRange)
  }

  useEffect(() => {
    if (!initialFacilities && data.length > 0) {
      const hotelsFacilities: { label: string; code: string }[] = []
      data?.forEach((entity) => {
        entity.entityBasicDetails.facilities.forEach((f) =>
          hotelsFacilities.push({
            code: f.ID,
            label: f.Name
          })
        )
      })

      const seen = new Set<string>()
      const uniqueHotelsFacilities: CheckBoxDataType[] = hotelsFacilities
        .filter((hf) => {
          if (seen.has(hf.code)) {
            return false
          } else {
            seen.add(hf.code)
            return true
          }
        })
        .map((d) => ({
          title: d.label,
          value: d.code,
          checked: false
        }))

      setInitialFacilities(uniqueHotelsFacilities)
    }
  }, [data, initialFacilities])
  const uniqueHotelsFacilities = initialFacilities || []

  const handleResetAll = () => {
    setSelectedFacilities([])
    setSelectedPropertyType([])
    setselectedHotelStar([])
    setPriceRange([min, maxPrice])
  }

  return (
    <div className={classes.root}>
      <ChannelBalance channelName={params.name} />
      <div className={classes.filtersContainer}>
        <div className={classes.filterHeader}>
          <Typography variant="body2">{getTranslate('Filters')}</Typography>
          <Button variant="text" onClick={handleResetAll}>
            <Typography className={classes.reset} variant="h5">
              {getTranslate('Reset All')}
            </Typography>
          </Button>
        </div>
        <div className={classes.seprator}></div>
        <div className={classes.search}>
          <Typography
            variant="h5"
            style={{ marginBottom: 10 }}
            className={classes.filterText}>
            {getTranslate('Search By Name')}
          </Typography>
          <InputWithLabel
            placeholder={getTranslate('Enter Hotel Name')}
            inputSize="small"
            onChange={(event) => {
              setsearchInputVal(
                convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
              )
            }}
            startIcon={<SearchIcon />}
          />
        </div>
        <PriceSlider
          label={getTranslate('Price Range For 1 Night')}
          currency={searchAvailabilityInput.currency}
          min={min}
          max={maxPrice}
          price={priceRange}
          setPrice={handlePriceChange}
        />
        <div className={classes.starRatingContainer}>
          <Typography variant="h5" className={classes.filterText}>
            {getTranslate('Star Rating')}
          </Typography>
          <FormGroup className={classes.ratingForm}>
            {[5, 4, 3, 2, 1].map((ele, idx) => (
              <div className={classes.starRow} key={idx}>
                <Rating value={ele} max={ele} readOnly size="small" />
                <FormControlLabel
                  label=""
                  classes={{ root: classes.check }}
                  control={
                    <Checkbox
                      color="primary"
                      size="small"
                      className={classes.checkbox}
                      value={selectedHotelStar.includes(ele)}
                      checked={selectedHotelStar.includes(ele)}
                      onChange={() => handleHotelStarchecked(ele)}
                    />
                  }
                />
              </div>
            ))}
          </FormGroup>
        </div>
        <ContentFitList
          header={getTranslate('Property Type')}
          options={propertyTypeOptions.map((r) => ({
            value: r,
            label: r
          }))}
          selectedItems={selectedPropertyType}
          setSelectedItems={setSelectedPropertyType}
        />
        {uniqueHotelsFacilities.length > 0 && (
          <CustomCheckBox
            header="Facilities"
            options={uniqueHotelsFacilities}
            selectedItems={selectedFacilities}
            setSelectedItems={setSelectedFacilities}
          />
        )}
      </div>
    </div>
  )
}

export default HotelFilters
